import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getToken } from '@/lib/auth'
import { useAppStore } from '@/store'

type EnvironmentState = {
  success: boolean
  environment_mode: 'live' | 'sandbox'
  active_sandbox_id: string | null
  sandbox?: { name?: string; sandbox_id?: string } | null
}

async function environmentRequest(init?: RequestInit): Promise<EnvironmentState> {
  const token = getToken()
  const res = await fetch('/api/dashboard/sandbox/environment', {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })
  const body = await res.json()
  if (!res.ok) throw new Error(body.message || body.error || 'Environment status unavailable')
  return body
}

export function EnvironmentModeBanner() {
  const environmentMode = useAppStore((s) => s.environmentMode)
  const setEnvironmentMode = useAppStore((s) => s.setEnvironmentMode)
  const [sandboxName, setSandboxName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const sandbox = environmentMode === 'sandbox'

  async function sync() {
    try {
      const state = await environmentRequest()
      if (!['live', 'sandbox'].includes(state.environment_mode)) throw new Error('Environment status unavailable')
      setError('')
      setEnvironmentMode(state.environment_mode)
      setSandboxName(state.sandbox?.name || state.active_sandbox_id || null)
      document.cookie = `ops_environment=${state.environment_mode}; Path=/; SameSite=Strict; Max-Age=31536000`
    } catch {
      // Preserve last confirmed state. A failed read does not establish Live mode.
      setError('Environment status could not be refreshed. Last confirmed state retained.')
    }
  }

  useEffect(() => {
    void sync()
    const listener = () => void sync()
    window.addEventListener('ops-sandbox-change', listener)
    window.addEventListener('focus', listener)
    return () => {
      window.removeEventListener('ops-sandbox-change', listener)
      window.removeEventListener('focus', listener)
    }
  }, [])

  async function returnLive() {
    if (busy) return
    const ok = window.confirm('Return to Live Mode? Actions can affect production resources.')
    if (!ok) return
    setBusy(true)
    try {
      const state = await environmentRequest({ method: 'POST', body: JSON.stringify({ environment_mode: 'live', sandbox_id: null }) })
      if (state.environment_mode !== 'live') throw new Error('The server did not confirm Live mode. Sandbox state retained.')
      setError('')
      setEnvironmentMode(state.environment_mode)
      setSandboxName(null)
      document.cookie = 'ops_environment=live; Path=/; SameSite=Strict; Max-Age=31536000'
      window.dispatchEvent(new CustomEvent('ops-sandbox-change', { detail: state }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Return to Live failed. Sandbox state retained.')
    } finally {
      setBusy(false)
    }
  }

  if (!sandbox && !error) return null

  return (
    <div className={`fixed inset-x-0 ${sandbox ? 'top-0' : 'bottom-0'} z-[140] flex h-12 items-center border-b border-amber-300/60 bg-amber-400/95 px-3 text-slate-950 shadow-lg backdrop-blur-xl`} style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
      <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="truncate text-[10px] font-bold uppercase tracking-[0.16em] sm:text-[11px]">
            {error ? error : `SANDBOX MODE${sandboxName ? ' — ' + sandboxName : ''} — PRODUCTION WRITES BLOCKED`}
          </span>
        </div>
        <button type="button" onClick={() => error ? void sync() : void returnLive()} disabled={busy} className="min-h-11 shrink-0 rounded-full border border-slate-950/25 bg-slate-950/10 px-3 py-1 text-[11px] font-semibold tracking-[0.02em] transition hover:bg-slate-950/20 disabled:opacity-40 sm:text-[12px]">
          {error ? 'Refresh status' : busy ? 'Switching…' : 'Return to Live'}
        </button>
      </div>
    </div>
  )
}
